/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  Dispatch,
  SyntheticEvent,
  useCallback,
  useEffect,
} from 'react';
import useGetAutopayStatusQid from '../common/hooks/useGetAutopayStatusQid';
import useGetEftBankAcctTypeQid from '../common/hooks/useGetEftBankAcctTypeQid';
import useGetAuthMethodQid from '../common/hooks/useGetAuthMethodQid';
import useGetAutopayInfo from '../common/hooks/useGetAutopayInfo';
import useGetPaymentInfo from '../common/hooks/useGetPaymentInfo';
import useGetMnthlyImp from '../common/hooks/useGetMnthlyImp';
import useAbortController from '../common/hooks/useAbortController';
import useGetServicingPayment from '../common/hooks/useGetServicingPayment';
import axiosCall from '../services/axios';
import dayjs from 'dayjs';
import * as constants from '../common/constants';
import { isStrEmptyOrNull, isValidRoutingNumber } from '../common/UtilityFunctions';
import { numericFormatter } from 'react-number-format';
import { useLoanContext } from './LoanContext';
import { useAuthContext } from './AuthContext';
import { useBrwrContext } from './BorrowerContext';
import { useCommunicationContext } from './CommunicationContext';
import useQidList from '../common/hooks/useQidList';
import useGetServicingInstallments from '../common/hooks/useGetServicingInstallments';

type reduceAutopayState = {
  authorizationDate: null;
  eftRoutingNo: string;
  additionalPrincipal: number;
  additionalEscrow: number;
  eftBankAccountNo: string;
  eftBankName: string;
  startDate: null;
  eftBankAccountTypeKeyword: string;
  authorizationMethodKeyword: string;
  autoPayStatusKeyword: string;
  settleDay: string;
  otpRoutingNo: string;
  otpBankAccountNo: string;
  otpBankName: string;
  otpBankAccountTypeKeyword: string;
};

type reducePaymentState = {
  dueDate: null;
  apparPi: number;
};

type reduceLoanState = {
  lpi: number;
};

type statusAlertType = {
  status: string;
  message: string;
};

type qidType = {
  keyword: string;
  description: string;
};

type bankingDetailsType = {
  eftRoutingNo: string;
  eftBankAccountNo: string;
  eftBankName: string;
  eftBankAccountTypeKeyword: string;
};

type autopayValue = {
  autopayInfo: reduceAutopayState;
  paymentInfo: reducePaymentState;
  dispatchAutopayInfo: Dispatch<{}>;
  handleSubmit: (e: SyntheticEvent) => void;
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  autopayStatusQidInfo: Array<qidType>;
  eftBankAcctTypeQidInfo: Array<qidType>;
  authMethodQidInfo: Array<qidType>;
  eftRoutingNoError: boolean;
  setEftRoutingNoError: (boolean) => void;
  eftBankNameError: boolean;
  setEftBankNameError: (boolean) => void;
  eftBankAccountNoError: boolean;
  setEftBankAccountNoError: (boolean) => void;
  eftBankAccountTypeError: boolean;
  setEftBankAccountTypeError: (boolean) => void;
  startDateError: boolean;
  setStartDateError: (boolean) => void;
  settleDayError: boolean;
  setSettleDayError: (boolean) => void;
  settleDayCurrentMonthError: boolean;
  setSettleDayCurrentMonthError: (boolean) => void;
  settleDayRangeError: boolean;
  setSettleDayRangeError: (boolean) => void;
  additionalPrincipalError: boolean;
  setAdditionalPrincipalError: (boolean) => void;
  additionalEscrowError: boolean;
  setAdditionalEscrowError: (boolean) => void;
  autopayStatusError: boolean;
  setAutopayStatusError: (boolean) => void;
  authMethodError: boolean;
  setAuthMethodError: (boolean) => void;
  authDateError: boolean;
  setAuthDateError: (boolean) => void;
  retainInfo: boolean;
  setRetainInfo: (boolean) => void;
  autopayModalOpen: boolean;
  setAutopayModalOpen: (boolean) => void;
  autopayModalContent: string;
  setAutopayModalContent: (string) => void;
  latestPaymentDate: number;
  validateFields: (SyntheticEvent) => void;
  handleCancel: () => void;
  cancelAutopayModalOpen: boolean;
  setCancelAutopayModalOpen: (boolean) => void;
  borrowerAutopayStatus: string;
  borrowerInfo: BorrowerData;
  loanInfo: reduceLoanState;
  prevStartDate: null;
  setPrevStartDate: (string) => void;
  loadingMessage: string;
  payment: number;
  stopCodeST01: boolean;
  stopCodeST02: boolean;
  stopCodeST03: boolean;
  stopCodeST04: boolean;
  stopCodeST20: boolean;
  hasDisablingStopCode: boolean;
  handleRemoveBankDetails: () => void;
  originalAutopayStatus: string;
  originalBankingDetails: bankingDetailsType | null;
};

const AutopayContext = createContext<autopayValue | null>(null);

const useAutopayContext = () => {
  const autopayContext = useContext(AutopayContext);
  if (autopayContext === null) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('autopayContext must be inside a AutopayProvider');
  }
  return autopayContext;
};

type autopayProviderProps = {
  children: ReactNode;
};

const AutopayProvider = ({ children }: autopayProviderProps): JSX.Element => {
  const { loanNumber, primaryBorrowerNumber, lpi, servicingStopCodes, refetchLoanData } = useLoanContext();
  const { borrowerData, borrowerDataArr } = useBrwrContext();
  const { firstName, lastName, servicerUser, token, userId } = useAuthContext();
  const { get } = useAbortController();
  const { isCommenting, createComment } = useCommunicationContext();
  const { loadingQid: loadingCategories, qid: categories } = useQidList('CMT4_CD.027');
  const actionCodesQidListResponse = useQidList('CMT4_CD.025');
  const actionCodes = useMemo(() => actionCodesQidListResponse.qid, [actionCodesQidListResponse]);
  const resultCodesQidListResponse = useQidList('CMT4_CD.026');
  const resultCodes = useMemo(() => resultCodesQidListResponse.qid, [resultCodesQidListResponse]);

  const [commentData, setCommentData] = useState({
    userUid: userId,
    userName: ((firstName || '') + ' ' + (lastName || '')).trim(),
    addedByUid: userId,
    addedByUserName: ((firstName || '') + ' ' + (lastName || '')).trim(), // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: 'XX',
    actionCodeDescription: 'XX -',
    actionCodeKeyword: 'XX',
    codes: '',
    commentCategory: 'C',
    commentCategoryDescription: 'C - Collections Comment',
    commentCategoryKeyword: 'C',
    loanNumbers: `${loanNumber}`,
    loanNumber: `${loanNumber}`,
    text: '',
    promiseToPay: ``,
    promiseToPayAmount: '',
    resultCode: 'XX',
    resultCodeDescription: 'XX -',
    resultCodeKeyword: 'XX',
    success: '',
  });
  const [commentErrorData, setCommentErrorData] = useState({
    userUid: '',
    userName: '',
    addedByUserName: '', // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: '',
    actionCodeDescription: '',
    actionCodeKeyword: '',
    codes: '',
    commentCategory: '',
    commentCategoryDescription: '',
    commentCategoryKeyword: '',
    loanNumbers: '',
    loanNumber: '',
    text: '',
    promiseToPay: '',
    promiseToPayAmount: '',
    resultCode: '',
    resultCodeDescription: '',
    resultCodeKeyword: '',
    generic: '',
  });

  const borrowerInfo = useMemo(() => {
    if (!borrowerData || !primaryBorrowerNumber) return null;
    return borrowerData[primaryBorrowerNumber];
  }, [borrowerData, primaryBorrowerNumber]);

  const nextPayment = useMemo(() => {
    const nextPaymentDate = dayjs.unix(lpi).add(1, 'month');
    if (nextPaymentDate.isValid()) return nextPaymentDate.format('YYYYMMDD');
    return ''; // date is not valid
  }, [lpi]);

  const { autopayStatusQidInfo, autopayStatusQidStatus } = useGetAutopayStatusQid();
  const { eftBankAcctTypeQidInfo, eftBankAcctTypeQidStatus } = useGetEftBankAcctTypeQid();
  const { authMethodQidInfo, authMethodQidStatus } = useGetAuthMethodQid();
  const { autopayInfo, autopayStatus, dispatchAutopayInfo, refetchAutopayInfo } = useGetAutopayInfo(loanNumber);
  const { paymentInfo, paymentStatus } = useGetPaymentInfo(loanNumber, nextPayment);
  const { mnthlyImpInfo, mnthlyImpStatus } = useGetMnthlyImp(loanNumber, nextPayment);
  const { servInstData } = useGetServicingInstallments(loanNumber, nextPayment);
  const { servicingLoanPayments } = useGetServicingPayment(loanNumber);
  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });

  const [retainInfo, setRetainInfo] = useState(false);
  const [eftRoutingNoError, setEftRoutingNoError] = useState(false);
  const [eftBankNameError, setEftBankNameError] = useState(false);
  const [eftBankAccountNoError, setEftBankAccountNoError] = useState(false);
  const [eftBankAccountTypeError, setEftBankAccountTypeError] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [settleDayError, setSettleDayError] = useState(false);
  const [settleDayCurrentMonthError, setSettleDayCurrentMonthError] = useState(false);
  const [settleDayRangeError, setSettleDayRangeError] = useState(false);
  const [additionalPrincipalError, setAdditionalPrincipalError] = useState(false);
  const [additionalEscrowError, setAdditionalEscrowError] = useState(false);
  const [autopayStatusError, setAutopayStatusError] = useState(false);
  const [authMethodError, setAuthMethodError] = useState(false);
  const [authDateError, setAuthDateError] = useState(false);
  const [autopayModalOpen, setAutopayModalOpen] = useState(false);
  const [autopayModalContent, setAutopayModalContent] = useState('');
  const [cancelAutopayModalOpen, setCancelAutopayModalOpen] = useState(false);
  const [prevStartDate, setPrevStartDate] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [originalAutopayStatus, setOriginalAutopayStatus] = useState(null);
  const [originalBankingDetails, setOriginalBankingDetails] = useState<bankingDetailsType | null>(null);
  const status = useMemo(() => {
    const statuses = [
      autopayStatusQidStatus,
      eftBankAcctTypeQidStatus,
      authMethodQidStatus,
      autopayStatus,
      paymentStatus,
      submitStatus,
      mnthlyImpStatus,
    ];
    if (statuses.includes('error')) {
      return 'error';
    }
    if (statuses.includes('loading')) {
      return 'loading';
    }
    return 'done';
  }, [
    autopayStatusQidStatus,
    eftBankAcctTypeQidStatus,
    authMethodQidStatus,
    autopayStatus,
    paymentStatus,
    submitStatus,
    mnthlyImpStatus,
  ]);

  useEffect(() => {
    if (status === 'error') {
      setStatusAlert({
        status: 'error',
        message: 'Unable to retrieve autopay details.',
      });
    }
  }, [status]);

  const borrowerAutopayStatus = useMemo(() => {
    const status = autopayStatusQidInfo.find((option) => option.keyword === autopayInfo.autoPayStatusKeyword);
    if (!status) {
      return 'Not Active';
    } else if (status.keyword === 'YES') {
      return 'Active';
    }
    return status?.description;
  }, [autopayStatusQidInfo, autopayInfo]);

  const payment = useMemo(() => {
    return servInstData.borrowerPiti ?? 0;
  }, [servInstData.borrowerPiti]);

  const latestPaymentDate = useMemo(() => {
    if (!servicingLoanPayments) return null;
    const latestPayment = servicingLoanPayments
      .filter((payment) => payment.active && payment.posted && payment.mcdTransCode === '05A')
      .reduce((latest, payment) => {
        return !latest || new Date(payment.postDate) > new Date(latest.postDate) ? payment : latest;
      }, null);
    return latestPayment?.postDate;
  }, [servicingLoanPayments]);

  // Add all active stop codes to a set for quick lookup
  const stopCodeSet = useMemo(
    () => new Set(servicingStopCodes?.map((stopCode) => stopCode.servStopCodeKeyword)),
    [servicingStopCodes],
  );
  const stopCodeST01 = stopCodeSet.has('ST01');
  const stopCodeST02 = stopCodeSet.has('ST02');
  const stopCodeST03 = stopCodeSet.has('ST03');
  const stopCodeST04 = stopCodeSet.has('ST04');
  const stopCodeST20 = stopCodeSet.has('ST20');

  const hasDisablingStopCode = useMemo(() => stopCodeST04, [servicingStopCodes]);

  useEffect(() => {
    if (stopCodeST04) {
      setAutopayModalOpen(true);
      setAutopayModalContent('stopCodeST04');
    }
  }, [servicingStopCodes]);

  // Keep track of the original Autopay status while the user edits the form
  useEffect(() => {
    if (originalAutopayStatus === null && autopayInfo.autoPayStatusKeyword !== '') {
      setOriginalAutopayStatus(autopayInfo.autoPayStatusKeyword);
    }
  }, [autopayInfo, originalAutopayStatus]);

  // Keep track of the original banking details while the user edits the form
  useEffect(() => {
    if (
      originalBankingDetails === null &&
      autopayInfo !== null &&
      autopayInfo.eftRoutingNo !== '' &&
      autopayInfo.eftBankAccountNo !== '' &&
      autopayInfo.eftBankName !== '' &&
      autopayInfo.eftBankAccountTypeKeyword !== ''
    ) {
      setOriginalBankingDetails({
        eftRoutingNo: autopayInfo.eftRoutingNo,
        eftBankAccountNo: autopayInfo.eftBankAccountNo,
        eftBankName: autopayInfo.eftBankName,
        eftBankAccountTypeKeyword: autopayInfo.eftBankAccountTypeKeyword,
      });
    }
  }, [autopayInfo, originalBankingDetails]);

  const validateFields = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      const isCanceling = autopayInfo.autoPayStatusKeyword === 'CANCELED';
      let isError = false;
      if (!isCanceling && !isValidRoutingNumber(autopayInfo.eftRoutingNo)) {
        setEftRoutingNoError(true);
        isError = true;
      } else {
        setEftRoutingNoError(false);
      }
      if (!isCanceling && isStrEmptyOrNull(autopayInfo.eftBankName)) {
        setEftBankNameError(true);
        isError = true;
      } else {
        setEftBankNameError(false);
      }
      if (!isCanceling && isStrEmptyOrNull(autopayInfo.eftBankAccountNo)) {
        setEftBankAccountNoError(true);
        isError = true;
      } else {
        setEftBankAccountNoError(false);
      }
      if (!isCanceling && !autopayInfo.eftBankAccountTypeKeyword) {
        setEftBankAccountTypeError(true);
        isError = true;
      } else {
        setEftBankAccountTypeError(false);
      }
      if (
        !isCanceling &&
        (!autopayInfo.startDate ||
          dayjs.unix(autopayInfo.startDate).get('date') !== 1 ||
          (prevStartDate &&
            autopayInfo.startDate !== prevStartDate &&
            dayjs().isAfter(dayjs.unix(autopayInfo.startDate), 'month')))
      ) {
        setStartDateError(true);
        isError = true;
      } else {
        setStartDateError(false);
      }
      if (!isCanceling && !autopayInfo.settleDay) {
        setSettleDayError(true);
        isError = true;
      } else if (
        !isCanceling &&
        dayjs().isSame(dayjs.unix(autopayInfo.startDate), 'month') &&
        dayjs().get('date') > autopayInfo.settleDay
      ) {
        setSettleDayCurrentMonthError(true);
        isError = true;
      } else if (!isCanceling && autopayInfo.autoPayStatusKeyword !== 'PREPAY' && autopayInfo.settleDay > 16) {
        isError = true;
        setSettleDayRangeError(true);
      } else {
        setSettleDayError(false);
        setSettleDayCurrentMonthError(false);
        setSettleDayRangeError(false);
      }
      if (
        !isCanceling &&
        (autopayInfo.additionalPrincipal === null ||
          autopayInfo.additionalPrincipal === '' ||
          isNaN(autopayInfo.additionalPrincipal))
      ) {
        setAdditionalPrincipalError(true);
        isError = true;
      } else {
        setAdditionalPrincipalError(false);
      }
      if (
        !isCanceling &&
        (autopayInfo.additionalEscrow === null ||
          autopayInfo.additionalEscrow === '' ||
          isNaN(autopayInfo.additionalEscrow))
      ) {
        setAdditionalEscrowError(true);
        isError = true;
      } else {
        setAdditionalEscrowError(false);
      }
      if (servicerUser) {
        if (!autopayInfo.autoPayStatusKeyword) {
          setAutopayStatusError(true);
          isError = true;
        } else {
          setAutopayStatusError(false);
        }
        if (!autopayInfo.authorizationMethodKeyword) {
          setAuthMethodError(true);
          isError = true;
        } else {
          setAuthMethodError(false);
        }
        if (!autopayInfo.authorizationDate) {
          setAuthDateError(true);
          isError = true;
        } else {
          setAuthDateError(false);
        }
      }
      if (isError) {
        return;
      } else {
        //No Errors, Show Confirmation Modal
        setAutopayModalContent('confirm');
        setAutopayModalOpen(true);
      }
    },
    [
      autopayInfo.additionalEscrow,
      autopayInfo.additionalPrincipal,
      autopayInfo.authorizationDate,
      autopayInfo.authorizationMethodKeyword,
      autopayInfo.autoPayStatusKeyword,
      autopayInfo.eftBankAccountNo,
      autopayInfo.eftBankAccountTypeKeyword,
      autopayInfo.eftBankName,
      autopayInfo.eftRoutingNo,
      autopayInfo.settleDay,
      autopayInfo.startDate,
      servicerUser,
      prevStartDate,
    ],
  );

  // add a comment to the loan when autopay is set up or updated
  const handleAddComment = useCallback(
    async (e: SyntheticEvent, updatedAutopayInfo: reduceAutopayState) => {
      if (!token || !loanNumber) return;
      try {
        let comment = {
          ...commentData,
          text: `Auto Pay Confirmation email sent. Auto Pay has been set up with amount of ${numericFormatter(
            payment.toString(),
            { thousandSeparator: true, prefix: '$', decimalScale: 2, fixedDecimalScale: true },
          )} from account number ${updatedAutopayInfo.eftBankAccountNo}. The payment will be drafted effective ${dayjs.unix(updatedAutopayInfo.startDate).format('MM/DD/YYYY')} on the ${updatedAutopayInfo.settleDay} of each month. All Auto Pay set up disclosures have been provided to the borrower`,
        };
        await createComment(
          e,
          comment,
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          false,
        );
        if (!isStrEmptyOrNull(commentErrorData.text)) {
          throw Error(`Error adding comment "${commentData.text}" on loan, error: ${commentErrorData.text}`);
        }
      } catch (error) {
        console.error(`Error: ${error.message}`);
      }
    },
    [
      commentData,
      actionCodes,
      categories,
      resultCodes,
      setCommentErrorData,
      setCommentData,
      loanNumber,
      token,
      createComment,
      payment,
    ],
  );

  // Add a comment to the loan when a change is made to Autopay
  const handleStatusComment = useCallback(
    async (e: SyntheticEvent, updatedAutopayInfo: reduceAutopayState) => {
      if (!token || !loanNumber) return;
      try {
        let comment = {
          ...commentData,
          text: `Autopay status has been updated to: ${updatedAutopayInfo.autoPayStatusKeyword}`,
        };
        await createComment(
          e,
          comment,
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          false,
        );
        if (!isStrEmptyOrNull(commentErrorData.text)) {
          throw Error(`Error adding comment "${commentData.text}" on loan, error: ${commentErrorData.text}`);
        }
      } catch (error) {
        console.error(`Error: ${error.message}`);
      }
    },
    [
      commentData,
      actionCodes,
      categories,
      resultCodes,
      setCommentErrorData,
      setCommentData,
      loanNumber,
      token,
      createComment,
      ,
    ],
  );

  const handleConfirmationEmail = useCallback(
    async (isUpdate: boolean, updatedAutopayInfo: reduceAutopayState) => {
      if (!token || !loanNumber) return;
      const controller = get();
      const config = { signal: controller.signal };
      try {
        const authMethod = authMethodQidInfo.find(
          (option) => option.keyword === updatedAutopayInfo.authorizationMethodKeyword,
        )?.description;
        const authMethodText = authMethod ? authMethod : 'Online';
        const authDate = updatedAutopayInfo.authorizationDate
          ? dayjs.unix(updatedAutopayInfo.authorizationDate).format('MM/DD/YYYY')
          : dayjs().format('MM/DD/YYYY');
        let requestBody = {
          to: borrowerDataArr
            .filter((b) => b.borrowerOnNote && b.borrowerType !== 'R')
            .map((b) => b.emailAddress)
            .join(','),
          headerMap: {
            SUBJECT: `${loanNumber} Autopay ${isUpdate ? 'Update' : 'Confirmation'} Notification`,
          },
          bodyMap: {
            ACTIONED: 'updated',
            BANK: updatedAutopayInfo.eftBankName,
            ROUTING_NUM: updatedAutopayInfo.eftRoutingNo,
            ACCNT_LAST_FOUR: updatedAutopayInfo.eftBankAccountNo.slice(-4),
            START_DATE: dayjs.unix(updatedAutopayInfo.startDate).format('MM/DD/YYYY'),
            PMT_AMT: numericFormatter(payment.toString(), {
              thousandSeparator: true,
              decimalScale: 2,
              fixedDecimalScale: true,
            }),
            DRAFT_DATE: updatedAutopayInfo.settleDay,
            PRIN_AMT: numericFormatter(updatedAutopayInfo.additionalPrincipal.toString(), {
              thousandSeparator: true,
              decimalScale: 2,
              fixedDecimalScale: true,
            }),
            ESC_AMT: numericFormatter(updatedAutopayInfo.additionalEscrow.toString(), {
              thousandSeparator: true,
              decimalScale: 2,
              fixedDecimalScale: true,
            }),
            AUTH_METHOD: authMethodText,
            AUTH_DATE: authDate,
          },
          isHtmlMsg: true,
        };
        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };
        await axiosCall(
          { url: `live/email-template/RCS Autopay Update Confirmation/send`, method: 'post', data: requestBody },
          config,
          headers,
        );
      } catch (e) {
        console.error(e);
      }
    },
    [loanNumber, borrowerInfo, borrowerDataArr, authMethodQidInfo, payment, get, token],
  );

  const handleCancel = useCallback(async () => {
    if (!token || !loanNumber) return;
    setCancelAutopayModalOpen(false);
    setSubmitStatus('loading');
    setLoadingMessage('Canceling...');
    const controller = get();
    const config = { signal: controller.signal };
    try {
      const requestBody = {
        autoPayStatusKeyword: 'CANCELED',
      };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      let response = await axiosCall(
        { url: `live/loan/${loanNumber}/servicing`, method: 'post', data: requestBody },
        config,
        headers,
      );
      setSubmitStatus('done');
      setLoadingMessage('');
      if (response) {
        dispatchAutopayInfo({ type: 'changed_autopayInfo', autopayInfo: response });
        refetchLoanData();
        setStatusAlert({
          status: 'success',
          message: 'Autopay Canceled',
        });
      }
    } catch (e) {
      setSubmitStatus('done');
      setLoadingMessage('');
      console.error(e);
      setStatusAlert({
        status: 'error',
        message: `Failed to cancel autopay info: ${e}`,
      });
    }
  }, [loanNumber, dispatchAutopayInfo, borrowerInfo, get, token, refetchLoanData]);

  const handleSubmit = useCallback(
    async (e: SyntheticEvent) => {
      if (!token || !loanNumber) return;
      setAutopayModalOpen(false);
      setSubmitStatus('loading');
      setLoadingMessage('Saving...');
      const controller = get();
      const config = { signal: controller.signal };
      try {
        let requestBody = {
          ...autopayInfo,
          eftBankAccountNo: autopayInfo.eftBankAccountNo.replace(/\s/g, ''),
          additionalPrincipal: autopayInfo.additionalPrincipal,
          additionalEscrow: autopayInfo.additionalEscrow,
          active: true,
        };
        //needed so keywords take priority
        delete requestBody.autoPayStatus;
        delete requestBody.authorizationMethod;
        delete requestBody.eftBankAccountType;
        delete requestBody.otpBankAccountType;
        if (retainInfo) {
          requestBody = {
            ...requestBody,
            otpRoutingNo: autopayInfo.eftRoutingNo,
            otpBankAccountNo: autopayInfo.eftBankAccountNo.replace(/\s/g, ''),
            otpBankName: autopayInfo.eftBankName,
            otpBankAccountTypeKeyword: autopayInfo.eftBankAccountTypeKeyword,
          };
        }
        if (!servicerUser) {
          requestBody.autoPayStatusKeyword = 'YES';
          requestBody.authorizationMethodKeyword = 'ONLINE';
          requestBody.authorizationDate = dayjs().unix();
        }

        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };
        let response = await axiosCall(
          { url: `live/loan/${loanNumber}/servicing`, method: 'post', data: requestBody },
          config,
          headers,
        );
        setSubmitStatus('done');
        setLoadingMessage('');
        if (response) {
          const updatedAutopayInfo: reduceAutopayState = response;

          dispatchAutopayInfo({ type: 'changed_autopayInfo', autopayInfo: updatedAutopayInfo });
          setStatusAlert({
            status: 'success',
            message: 'Autopay Info Updated!',
          });

          // send confirmation/update emails based on the autopay flag.
          if (
            updatedAutopayInfo.autoPayStatusKeyword === 'YES' ||
            updatedAutopayInfo.autoPayStatusKeyword === 'PREPAY'
          ) {
            const isUpdate = originalAutopayStatus === 'YES' || originalAutopayStatus === 'PREPAY';

            await handleConfirmationEmail(isUpdate, updatedAutopayInfo);
            await handleAddComment(e, updatedAutopayInfo);
          } else {
            await handleStatusComment(e, updatedAutopayInfo);
          }

          setOriginalAutopayStatus(updatedAutopayInfo.autoPayStatusKeyword);
        }
      } catch (err) {
        setSubmitStatus('done');
        setLoadingMessage('');
        console.error(err);
        setStatusAlert({
          status: 'error',
          message: `Failed to update autopay info: ${err}`,
        });
      }
    },
    [
      autopayInfo,
      loanNumber,
      retainInfo,
      dispatchAutopayInfo,
      originalAutopayStatus,
      borrowerInfo,
      handleConfirmationEmail,
      handleAddComment,
      handleStatusComment,
      get,
      servicerUser,
      token,
    ],
  );

  // Set the bank account fields to blank
  const handleRemoveBankDetails = useCallback(async () => {
    if (!token || !loanNumber) return;
    setSubmitStatus('loading');

    try {
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const requestBody = {
        eftRoutingNo: '',
        eftBankAccountNo: '',
        eftBankAccountTypeKeyword: '',
        eftBankName: '',
        active: true,
      };
      const response = await axiosCall(
        { url: `live/loan/${loanNumber}/servicing`, method: 'post', data: requestBody },
        config,
        headers,
      );
      if (!response || response?.error) {
        throw Error('Unable to save bank details. ' + response?.error);
      }
    } catch (e) {
      console.error(e);
      setStatusAlert({
        status: 'error',
        message: `Failed to save bank details: ${e}`,
      });
    }
    setSubmitStatus('done');
    await refetchAutopayInfo();
    setAutopayModalOpen(false);
    setOriginalBankingDetails(null);
  }, [loanNumber, token, get, refetchAutopayInfo]);

  return (
    <AutopayContext.Provider
      value={{
        autopayInfo,
        paymentInfo,
        dispatchAutopayInfo,
        handleSubmit,
        status,
        statusAlert,
        setStatusAlert,
        autopayStatusQidInfo,
        eftBankAcctTypeQidInfo,
        authMethodQidInfo,
        eftRoutingNoError,
        setEftRoutingNoError,
        eftBankNameError,
        setEftBankNameError,
        eftBankAccountNoError,
        setEftBankAccountNoError,
        eftBankAccountTypeError,
        setEftBankAccountTypeError,
        startDateError,
        setStartDateError,
        settleDayError,
        setSettleDayError,
        settleDayCurrentMonthError,
        setSettleDayCurrentMonthError,
        settleDayRangeError,
        setSettleDayRangeError,
        additionalPrincipalError,
        setAdditionalPrincipalError,
        additionalEscrowError,
        setAdditionalEscrowError,
        autopayStatusError,
        setAutopayStatusError,
        authMethodError,
        setAuthMethodError,
        authDateError,
        setAuthDateError,
        retainInfo,
        setRetainInfo,
        autopayModalOpen,
        setAutopayModalOpen,
        autopayModalContent,
        setAutopayModalContent,
        latestPaymentDate,
        validateFields,
        handleCancel,
        cancelAutopayModalOpen,
        setCancelAutopayModalOpen,
        borrowerAutopayStatus,
        borrowerInfo,
        loanInfo: { lpi: lpi },
        prevStartDate,
        setPrevStartDate,
        loadingMessage,
        payment,
        stopCodeST01,
        stopCodeST02,
        stopCodeST03,
        stopCodeST04,
        stopCodeST20,
        hasDisablingStopCode,
        handleRemoveBankDetails,
        originalAutopayStatus,
        originalBankingDetails,
      }}
    >
      {children}
    </AutopayContext.Provider>
  );
};

export { AutopayProvider, useAutopayContext };
